import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { NotificationForegroundMessageWire } from 'src/api/generated';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useRealtime } from 'src/modules/shared/realtime/hooks/provider-bounded/use-realtime';
import { useNotificationsEndpoints } from '../api/notifications';
import {
  getExistingWebPushEndpoint,
  getWebPushDeviceId,
  hasWebPushVapidKey,
  isWebPushSupported,
} from './use-web-push';

/** Advertises only a focused browser with a ready notification socket; the server bounds expiry. */
export const useBrowserNotificationPresence = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const { user } = useCurrentUser(isAuthenticated && !isLoading);
  const { status, topicStatuses, sendControl } = useRealtime();
  const canUsePush = hasWebPushVapidKey() && isWebPushSupported();
  const canAdvertise = canUsePush && Notification.permission === 'granted';
  const { data: endpoints } = useNotificationsEndpoints(
    canAdvertise && isAuthenticated && !isLoading
  );
  const clientId = useRef<string | null>(null);
  const observedAt = useRef(0);
  const [subscription, setSubscription] = useState<{
    /** Endpoint query snapshot that initiated this browser lookup. */
    endpoints: typeof endpoints;
    /** Stable browser installation identity. */
    deviceId: string;
    /** Actual subscription URL owned by the current browser. */
    url: string;
  } | null>(null);
  useEffect(() => {
    if (!canAdvertise || !isAuthenticated || isLoading || !endpoints) return;
    let cancelled = false;
    const readSubscription = async () => {
      try {
        const deviceId = getWebPushDeviceId();
        const url = await getExistingWebPushEndpoint();
        if (!cancelled) {
          setSubscription(url ? { endpoints, deviceId, url } : null);
        }
      } catch {
        if (!cancelled) setSubscription(null);
      }
    };
    void readSubscription();
    return () => {
      cancelled = true;
    };
  }, [canAdvertise, isAuthenticated, isLoading, endpoints]);
  const endpointId = endpoints?.find(
    (endpoint) =>
      subscription?.endpoints === endpoints &&
      endpoint.isEnabled &&
      endpoint.platform === 'WEB_PUSH' &&
      endpoint.deviceId === subscription.deviceId &&
      endpoint.webPush?.endpoint === subscription.url &&
      endpoint.tenantId === user?.tenantId &&
      endpoint.userId === String(user.id)
  )?.id;
  const ready =
    isAuthenticated &&
    !isLoading &&
    status === 'connected' &&
    topicStatuses['notifications.inbox'] === 'subscribed';

  useEffect(() => {
    if (!endpointId || !canAdvertise) return;
    const tabId = clientId.current ?? crypto.randomUUID();
    clientId.current = tabId;
    let lastState: boolean | undefined;
    let lastSentAt = 0;
    const observe = (isForeground: boolean, force = false) => {
      const now = Date.now();
      if (
        !force &&
        lastState === isForeground &&
        (!isForeground || now - lastSentAt < 1000)
      ) {
        return;
      }
      observedAt.current = Math.max(now, observedAt.current + 1);
      const message: NotificationForegroundMessageWire = {
        type: NotificationForegroundMessageWire.type.NOTIFICATIONS_FOREGROUND,
        payload: {
          endpointId,
          clientId: tabId,
          observedAt: observedAt.current,
          isForeground,
        },
      };
      if (!sendControl(message)) return;
      lastState = isForeground;
      lastSentAt = now;
    };
    const refresh = () =>
      observe(
        ready && document.visibilityState === 'visible' && document.hasFocus()
      );
    const hide = () => observe(false);
    refresh();
    const interval = window.setInterval(() => {
      if (
        ready &&
        document.visibilityState === 'visible' &&
        document.hasFocus()
      ) {
        observe(true);
      }
    }, 30_000);
    window.addEventListener('focus', refresh);
    window.addEventListener('blur', hide);
    window.addEventListener('pagehide', hide);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('blur', hide);
      window.removeEventListener('pagehide', hide);
      document.removeEventListener('visibilitychange', refresh);
      observe(false, true);
    };
  }, [endpointId, canAdvertise, ready, sendControl]);
};
